import { Fingerprint } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { usePasskeyLogin } from "@/hooks/auth/usePasskeyQueries";

/**
 * PasskeyLoginButton
 *
 * Placed on the login page below the existing "Sign In" button, separated
 * by an "or" divider. Triggers the full WebAuthn authentication ceremony
 * (including cross-device QR code flow) via the browser's native prompt.
 */
export default function PasskeyLoginButton({ mode = "default" }) {
  const { mutate: loginWithPasskey, isPending } = usePasskeyLogin();
  const isHero = mode === "hero";
  const buttonClassName = isHero
    ? "group relative flex w-full items-center justify-center gap-2.5 rounded-2xl border border-white/85 bg-white/95 py-4 text-lg font-semibold text-[#1f2a44] shadow-[0_0_0_1px_rgba(255,255,255,0.85),0_20px_48px_rgba(59,130,246,0.2)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_0_1px_rgba(255,255,255,0.95),0_28px_56px_rgba(20,184,166,0.25)] disabled:opacity-70"
    : "flex w-full cursor-pointer items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold sm:py-4 sm:text-base lg:py-5 lg:text-lg";
  const iconClassName = isHero ? "h-5 w-5 text-[#1f2a44] lg:h-5 lg:w-5" : "h-4 w-4 lg:h-5 lg:w-5";

  return (
    <Button
      type="button"
      variant="outline"
      onClick={() => loginWithPasskey()}
      disabled={isPending}
      className={buttonClassName}>
      {isHero && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-2xl bg-linear-to-r from-blue-100/35 via-transparent to-emerald-100/35 opacity-70 transition-opacity duration-300 group-hover:opacity-100"
        />
      )}
      {isPending ? (
        <>
          <Spinner className={iconClassName} />
          <span>Waiting for passkey...</span>
        </>
      ) : (
        <>
          <Fingerprint className={iconClassName} />
          <span>Sign in with Passkey</span>
        </>
      )}
    </Button>
  );
}
